/* Organizer roles — app-wide roles gating what a user may do in this app
   (designs/organizer-roles.md R1). Distinct from project roles
   (src/shared/roles.ts), which are the slots a project needs filled. */

export interface OrganizerRole {
  id: string;
  name: string;
  /* R12 — a "granted" role is assigned by an Admin in the app; a "derived"
     role comes from the team roster (you are Dev Manager of exactly the
     teams whose recorded manager you are) and is never granted or revoked
     here */
  assignment: "granted" | "derived";
}

export const ORGANIZER_ROLES: OrganizerRole[] = [
  { id: "admin", name: "Admin", assignment: "granted" },
  { id: "dev-manager", name: "Dev Manager", assignment: "derived" },
  /* maintains the record without holding Permissions
     (authorization-and-attribution.md AA1) */
  { id: "record-keeper", name: "Record Keeper", assignment: "granted" },
];

/* a derived role held via the roster — shown read-only with its source */
export interface DerivedRole {
  roleId: string;
  teamId: string;
  /* the roster's own stamp, e.g. "Entra ID · Aug 18, 2026" */
  source: string;
}

export interface UserRef {
  userId: string;
  name: string;
}

/* The effective-dating stamp every revision carries (settled decision 4).
   `by` is the acting user (authorization-and-attribution.md AA4) — absent on
   revisions written before attribution existed, an honest absence that is
   never backfilled. */
export interface Stamp {
  source: string;
  date: string;
  by?: UserRef;
  /* which path the write came through (intake-input-path.md IP3) — set on
     filings and backings only, absent everywhere else and on every row
     written before it existed: not recorded, never back-filled */
  via?: EntryPath;
}

/* web = the browser; claude = a request that offered a bearer, which is
   how Friday's connector arrives (intake-input-path.md IP1/IP2) */
export const ENTRY_PATHS = ["web", "claude"] as const;
export type EntryPath = (typeof ENTRY_PATHS)[number];

/* how a person reads each path (intake-input-path.md IP7) — Friday is the
   plumbing, Claude is what the person used */
export const ENTRY_PATH_LABEL: Record<EntryPath, string> = { web: "Web app", claude: "Claude" };
export const ENTRY_PATH_PHRASE: Record<EntryPath, string> = { web: "via the web app", claude: "via Claude" };

/* stamped when the user's first sign-in was recorded (R4) */
export const SIGNED_IN_SRC = "Signed in";

export interface AppUser extends UserRef {
  firstSeen: { source: string; date: string };
  /* learned at a web sign-in, where Entra signs it beside the object id
     (designs/one-identity.md OI5); absent = not known */
  email?: string;
}

export interface RoleGrant {
  userId: string;
  roleId: string;
  /* present exactly when the role is team-scoped (R10) */
  teamId?: string;
  grantedBy: UserRef;
  recorded: { source: string; date: string };
}
